(() => {
  const MAP_URL = window.__SIZE_ORDER_MAP_URL || "/assets/size-order-map.json";
  const SETTLE_MS = 90;

  const norm = (s) => (s || "").toString().trim();
  const normSpace = (s) => norm(s).replace(/\s+/g, " ");
  const isFiniteNum = (x) => Number.isFinite(x);
  const now = () => (window.performance?.now ? performance.now() : Date.now());

  const state = new WeakMap();
  const handled = new WeakSet();

  function getState(root) {
    let s = state.get(root);
    if (!s) {
      s = {
        adjusting: false,
        applying: false,
        fs: null,
        fsObs: null,
        rootObs: null,
        rootObsPending: false,
        availSig: "",
        lastOptIndex: null,
        lastDirtyAt: 0,
        settleTimer: null,
      };
      state.set(root, s);
    }
    return s;
  }

  function getSizeFieldset(root) {
    return root.querySelector("fieldset.product-form__input-size");
  }

  function hasSizeInputs(fs) {
    return !!fs && !!fs.querySelector('input[type="radio"]');
  }

  function setHideDefault(root, on) {
    if (!root) return;
    if (on) root.setAttribute("data-size-hide-default", "1");
    else root.removeAttribute("data-size-hide-default");
  }

  function isHideDefault(root) {
    return root?.getAttribute("data-size-hide-default") === "1";
  }

  function setFsReady(fs, ready) {
    if (!fs) return;
    if (ready) fs.setAttribute("data-size-ready", "1");
    else fs.removeAttribute("data-size-ready");
  }

  function setFsReadyInstant(fs) {
    if (!fs) return;
    fs.setAttribute("data-size-noanim", "1");
    fs.setAttribute("data-size-ready", "1");
    requestAnimationFrame(() => {
      if (fs && fs.isConnected) fs.removeAttribute("data-size-noanim");
    });
  }

  function getRank(map, raw) {
    if (!map) return null;

    const s0 = norm(raw);
    if (!s0) return null;

    if (map[s0] != null) return map[s0];

    const s1 = normSpace(s0);
    if (map[s1] != null) return map[s1];

    const dot = s0.replace(",", ".");
    if (map[dot] != null) return map[dot];

    const comma = s0.replace(".", ",");
    if (map[comma] != null) return map[comma];

    const dot2 = s1.replace(",", ".");
    if (map[dot2] != null) return map[dot2];

    const comma2 = s1.replace(".", ",");
    if (map[comma2] != null) return map[comma2];

    return null;
  }

  function sortValuesByMap(values, map) {
    const seen = new Set();
    const uniq = [];

    for (const v of values) {
      const s = norm(v);
      const k = s.toLowerCase();
      if (!s || seen.has(k)) continue;
      seen.add(k);
      uniq.push(s);
    }

    return uniq.sort((a, b) => {
      const ra = getRank(map, a);
      const rb = getRank(map, b);

      const A = isFiniteNum(ra) ? ra : 1e9;
      const B = isFiniteNum(rb) ? rb : 1e9;

      if (A !== B) return A - B;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
    });
  }

  function getDomSizeValues(fs) {
    const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
    return inputs.map((i) => norm(i.value)).filter(Boolean);
  }

  function isDomOrderCorrect(fs, map) {
    if (!fs) return true;
    if (!map) return true;

    const dom = getDomSizeValues(fs);
    const domSig = dom.map((v) => v.toLowerCase()).join("|");
    const sortedSig = sortValuesByMap(dom, map).map((v) => v.toLowerCase()).join("|");
    return domSig === sortedSig;
  }

  function applySizeOrderPure(root, map) {
    if (!map) return false;

    const fs = getSizeFieldset(root);
    if (!fs) return false;

    const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
    if (!inputs.length) return false;

    const pairs = inputs
      .map((inp) => {
        const label = fs.querySelector(`label[for="${CSS.escape(inp.id)}"]`);
        return { inp, label, value: norm(inp.value) };
      })
      .filter((p) => p.label && p.value);

    if (!pairs.length) return false;

    const curValues = pairs.map((p) => p.value);
    const sorted = sortValuesByMap(curValues, map);

    const nextSig = sorted.map((x) => x.toLowerCase()).join("|");
    const prevSig = fs.dataset.sizeOrderSig || "";
    if (prevSig === nextSig) return false;

    fs.dataset.sizeOrderSig = nextSig;

    const orderIndex = new Map();
    let i = 0;
    for (const v of sorted) {
      const key = norm(v).toLowerCase();
      if (!key || orderIndex.has(key)) continue;
      orderIndex.set(key, i++);
    }

    for (const p of pairs) {
      const key = p.value.toLowerCase();
      const ord = orderIndex.has(key) ? orderIndex.get(key) : 9999;
      p.label.style.setProperty("--size-order", String(ord));
    }

    return true;
  }

  function getVariantData(root) {
    const s =
      root.querySelector('script[type="application/json"][data-variant-json]') ||
      document.querySelector('script[type="application/json"][data-variant-json]');
    if (!s) return null;

    try {
      const parsed = JSON.parse(s.textContent || "[]");
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.variants)) return parsed.variants;
      return null;
    } catch {
      return null;
    }
  }

  function getOptionCount(root) {
    const inputs = root.querySelectorAll('input[type="radio"][data-option-index]');
    let max = -1;
    inputs.forEach((i) => {
      const n = Number(i.dataset.optionIndex);
      if (!Number.isNaN(n)) max = Math.max(max, n);
    });
    return max + 1;
  }

  function getSelected(root, optionCount) {
    const selected = new Array(optionCount).fill(null);
    for (let i = 0; i < optionCount; i++) {
      const checked = root.querySelector(`input[type="radio"][data-option-index="${i}"]:checked`);
      selected[i] = checked ? checked.value : null;
    }
    return selected;
  }

  function getVariantOptions(v, optionCount) {
    if (Array.isArray(v?.options) && v.options.length) return v.options;

    const o = [v?.option1, v?.option2, v?.option3];
    const out = new Array(optionCount).fill(null);
    for (let i = 0; i < optionCount; i++) out[i] = o[i] ?? null;
    return out;
  }

  function existsExactVariant(variants, selected, optionCount) {
    return variants.some((v) => {
      const opts = getVariantOptions(v, optionCount);
      for (let i = 0; i < optionCount; i++) {
        if (opts[i] !== selected[i]) return false;
      }
      return true;
    });
  }

  function findBestVariantForColor(variants, color, selected, optionCount, root) {
    const pool = variants.filter((v) => getVariantOptions(v, optionCount)[0] === color);
    if (!pool.length) return null;

    const hardDisabled = (optIndex, value) => {
      const input = root.querySelector(
        `input[type="radio"][data-option-index="${optIndex}"][value="${CSS.escape(value)}"]`
      );
      return input ? !!input.disabled : false;
    };

    let best = null;
    let bestScore = -1;
    let bestPenalty = 1e9;

    for (const v of pool) {
      const opts = getVariantOptions(v, optionCount);

      let score = 0;
      let penalty = 0;

      for (let i = 1; i < optionCount; i++) {
        if (selected[i] != null && opts[i] === selected[i]) score += 2;
        if (opts[i] != null && hardDisabled(i, opts[i])) penalty += 5;
      }

      if (v?.available) score += 1;

      if (score > bestScore || (score === bestScore && penalty < bestPenalty)) {
        best = v;
        bestScore = score;
        bestPenalty = penalty;
      }
    }

    return best;
  }

  function setRadiosToVariant(root, variant, optionCount) {
    const opts = getVariantOptions(variant, optionCount);
    let lastChanged = null;

    for (let i = 1; i < optionCount; i++) {
      const want = opts[i];
      if (want == null) continue;

      const input = root.querySelector(
        `input[type="radio"][data-option-index="${i}"][value="${CSS.escape(want)}"]`
      );
      if (!input) continue;

      if (!input.checked) {
        input.checked = true;
        lastChanged = input;
      }
    }

    return lastChanged;
  }

  function normalizeIfMissing(root) {
    const variants = getVariantData(root);
    if (!variants || !variants.length) return false;

    const optionCount = getOptionCount(root);
    if (!optionCount || optionCount < 2) return false;

    const selected = getSelected(root, optionCount);
    if (selected.some((x) => x == null)) return false;

    if (existsExactVariant(variants, selected, optionCount)) return false;

    const color = selected[0];
    if (!color) return false;

    const best = findBestVariantForColor(variants, color, selected, optionCount, root);
    if (!best) return false;

    const lastChanged = setRadiosToVariant(root, best, optionCount);
    if (!lastChanged) return false;

    lastChanged.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  }

  function computeAvailSig(root) {
    const fs = getSizeFieldset(root);
    if (!fs) return "";

    const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
    if (!inputs.length) return "no-inputs";

    return inputs.map((i) => `${norm(i.value).toLowerCase()}~${i.disabled ? "d1" : "d0"}`).join("|");
  }

  function scheduleSettle(root, map) {
    const s = getState(root);
    if (s.settleTimer) clearTimeout(s.settleTimer);
    s.settleTimer = setTimeout(() => trySettle(root, map), SETTLE_MS);
  }

  function trySettle(root, map) {
    const s = getState(root);
    s.settleTimer = null;

    if (!root.isConnected) return;

    if (now() - s.lastDirtyAt < SETTLE_MS - 5) {
      scheduleSettle(root, map);
      return;
    }

    const fs = getSizeFieldset(root);
    if (!fs) return;

    if (!hasSizeInputs(fs)) {
      s.lastDirtyAt = now();
      scheduleSettle(root, map);
      return;
    }

    const needsSort = !isDomOrderCorrect(fs, map);

    if (needsSort) {
      setHideDefault(root, true);
      setFsReady(fs, false);
      void fs.offsetWidth;
      s.applying = true;
      applySizeOrderPure(root, map);
      s.applying = false;
    }

    s.availSig = computeAvailSig(root);

    requestAnimationFrame(() => {
      if (!root.isConnected) return;
      const fs2 = getSizeFieldset(root);
      if (!fs2 || !hasSizeInputs(fs2)) return;

      if (!needsSort) setFsReadyInstant(fs2);
      else setFsReady(fs2, true);
    });
  }

  function onSizeMaybeChanged(root, map) {
    const s = getState(root);
    const fs = getSizeFieldset(root);
    if (!fs) return;

    if (!hasSizeInputs(fs)) return;

    const optionCount = getOptionCount(root);
    const lastOpt = s.lastOptIndex;

    if (optionCount <= 2 && lastOpt != null && lastOpt !== 0 && isDomOrderCorrect(fs, map)) {
      s.availSig = computeAvailSig(root);
      if (!fs.hasAttribute("data-size-ready")) setFsReadyInstant(fs);
      return;
    }

    const availNow = computeAvailSig(root);
    const sameAvail = availNow && availNow === s.availSig;

    if (sameAvail && isDomOrderCorrect(fs, map)) {
      if (!fs.hasAttribute("data-size-ready")) setFsReadyInstant(fs);
      return;
    }

    setHideDefault(root, true);
    s.lastDirtyAt = now();
    setFsReady(fs, false);
    scheduleSettle(root, map);
  }

  function ensureObservers(root, map) {
    const s = getState(root);
    const fs = getSizeFieldset(root);

    if (fs !== s.fs) {
      s.fs = fs;

      if (s.fsObs) s.fsObs.disconnect();
      s.fsObs = null;

      if (fs) {
        s.fsObs = new MutationObserver(() => {
          if (getState(root).applying) return;
          onSizeMaybeChanged(root, map);
        });

        s.fsObs.observe(fs, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ["disabled"],
        });

        if (!hasSizeInputs(fs)) {
          s.lastDirtyAt = now();
          setFsReady(fs, false);
          scheduleSettle(root, map);
        } else {
          const availNow = computeAvailSig(root);
          const sameAvail = availNow && availNow === s.availSig;

          if (!isHideDefault(root) && isDomOrderCorrect(fs, map) && (sameAvail || !s.availSig)) {
            s.availSig = availNow || s.availSig;
          } else if (isDomOrderCorrect(fs, map) && (sameAvail || !s.availSig)) {
            s.availSig = availNow || s.availSig;
            setFsReadyInstant(fs);
          } else {
            setHideDefault(root, true);
            s.lastDirtyAt = now();
            setFsReady(fs, false);
            scheduleSettle(root, map);
          }
        }
      }
    }

    if (!s.rootObs) {
      s.rootObs = new MutationObserver(() => {
        if (s.rootObsPending) return;
        s.rootObsPending = true;
        queueMicrotask(() => {
          s.rootObsPending = false;
          ensureObservers(root, map);
        });
      });

      s.rootObs.observe(root, { subtree: true, childList: true });
    }
  }

  function attachRoot(root, map) {
    if (!root || handled.has(root)) return;
    handled.add(root);

    ensureObservers(root, map);

    const fs = getSizeFieldset(root);
    if (fs) {
      if (isDomOrderCorrect(fs, map) && !isHideDefault(root)) {
      } else if (isDomOrderCorrect(fs, map)) {
        setFsReadyInstant(fs);
      } else {
        setHideDefault(root, true);
        setFsReady(fs, false);
        const s = getState(root);
        s.lastDirtyAt = now();
        scheduleSettle(root, map);
      }
    }

    root.addEventListener(
      "change",
      (e) => {
        const s = getState(root);
        if (s.adjusting) return;

        const t = e.target;
        if (!(t instanceof HTMLInputElement)) return;
        if (t.type !== "radio") return;
        if (t.dataset.optionIndex == null) return;

        ensureObservers(root, map);
        s.lastOptIndex = Number(t.dataset.optionIndex);

        queueMicrotask(() => {
          if (!root.isConnected) return;
          if (s.adjusting) return;
          s.adjusting = true;
          normalizeIfMissing(root);
          s.adjusting = false;
        });
      },
      true
    );
  }

  function scanAll(map) {
    document.querySelectorAll("variant-selects, variant-radios").forEach((r) => attachRoot(r, map));
  }

  function observeRerenders(map) {
    let pending = false;

    const obs = new MutationObserver((muts) => {
      if (pending) return;
      pending = true;

      queueMicrotask(() => {
        pending = false;

        for (const m of muts) {
          for (const n of m.addedNodes) {
            if (!(n instanceof Element)) continue;

            if (n.matches?.("variant-selects, variant-radios")) attachRoot(n, map);
            n.querySelectorAll?.("variant-selects, variant-radios").forEach((x) => attachRoot(x, map));
          }
        }
      });
    });

    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  let mapPromise = null;
  function loadMapOnce() {
    if (mapPromise) return mapPromise;

    mapPromise = fetch(MAP_URL, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

    return mapPromise;
  }

  document.addEventListener("DOMContentLoaded", async () => {
    const map = await loadMapOnce();
    if (!map) console.warn("[variant-ui-sync] size-order-map.json not loaded. Size sorting skipped.");

    scanAll(map);
    observeRerenders(map);

    document.addEventListener("shopify:section:load", () => scanAll(map));
    document.addEventListener("shopify:section:reorder", () => scanAll(map));
  });
})();
